// The ASSISTED persistence adapter — the session-authenticated implementation
// of the seam in prequalDraftAdapter.ts.
//
// Decision 18: the broker's form IS the client's form. The funnel pages are
// reused UNCHANGED and are handed this adapter as a prop; they never learn that
// they are being driven by somebody else, and — critically — they are never
// handed a token. A token surfaced into a staff or portal UI turns a
// screen-share, a screenshot or a support ticket into a permanent credential
// leak, and forking the form would break decision 18 on the first divergent bug
// fix.
//
// ONE adapter for BOTH the staff surface and the portal surface. They differ
// only in which HTTP client can reach which route, so that difference is a
// TRANSPORT passed in. Two copies of this file is how the two surfaces end up
// disagreeing about what a conflict looks like, with only one of them tested.

import { useMemo, useRef } from "react";
import type {
  PrequalFinalizeRequest,
  PrequalFinalizeResult,
  PrequalRole,
} from "../../api/prequalification";
import type {
  PrequalAnswers,
  PrequalDraftAdapter,
  PrequalDraftLoad,
  PrequalDraftSnapshot,
  PrequalDraftWriteResult,
} from "./prequalDraftAdapter";

/** How this application was completed. The choice the assisting human makes at
 *  the certification step, and never a default that attests to anything. */
export type AssistedCompletionMode = "SameSittingClientDevice" | "SameSittingBrokerCertified";

/** The certification state the surface owns and the adapter reads AT CALL TIME
 *  — never a value captured when the adapter was built, which would send
 *  whatever was selected first. */
export interface AssistedCertification {
  completionMode: AssistedCompletionMode;
  /** **Polarity.** The checkbox reads "email a copy to the client" and is
   *  defaulted ON, so UNCHECKING it makes this true. False SENDS. */
  suppressClientCopy: boolean;
}

/** What a load returns, in the shape both transports already have. */
export interface AssistedDraftLoaded {
  role: PrequalRole;
  firstName: string;
  lastName: string;
  email: string;
  partnerSlug?: string | null;
  stepIndex: number;
  stepCount: number;
  answersJson: string;
  rowVersion: string | null;
}

export interface AssistedStepBody {
  rowVersion: string | null;
  stepIndex: number;
  answersJson: string;
}

export interface AssistedFinalizeBody {
  rowVersion: string | null;
  consentAccepted: boolean;
  answersJson: string;
  phone?: string;
  website?: string;
  completionMode: AssistedCompletionMode;
  suppressClientCopy: boolean;
}

/**
 * The per-surface half: which client, and how that client reports a conflict.
 *
 * <b>`isConflict` is a transport concern on purpose.</b> The two apps throw
 * different error classes, and a shared adapter that sniffed error messages
 * instead would silently degrade a 409 into a generic failure — turning the one
 * failure a user can act on (reload) into one they cannot.
 */
export interface AssistedDraftTransport {
  load(draftId: string): Promise<AssistedDraftLoaded>;
  step(draftId: string, body: AssistedStepBody): Promise<{ rowVersion: string | null }>;
  finalize(draftId: string, body: AssistedFinalizeBody): Promise<PrequalFinalizeResult>;
  isConflict(error: unknown): boolean;
}

export interface AssistedAdapterOptions {
  draftId: string;
  role: PrequalRole;
  transport: AssistedDraftTransport;
  /** Read at call time, not captured. */
  certification: AssistedCertification;
  /** Called with the load's own copy of who this application is for, so the
   *  surface can show the assisting human whose form they are driving. */
  onLoaded?: (loaded: AssistedDraftLoaded) => void;
}

const FRIENDLY_SAVE_ERROR = "We couldn't save that just now.";
const CONFLICT_MESSAGE = "This application was updated somewhere else. Reload it and try again.";

/** Never surface raw HTTP text; keep a genuine server message. */
function friendly(error: unknown): string {
  const raw = error instanceof Error ? error.message.trim() : "";
  return !raw || /HTTP \d+|Request failed|Forbidden/i.test(raw) ? FRIENDLY_SAVE_ERROR : raw;
}

function parseAnswers(answersJson: string): PrequalAnswers | null {
  try {
    const parsed: unknown = JSON.parse(answersJson);
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as PrequalAnswers)
      : null;
  } catch {
    return null;
  }
}

export function useAssistedPrequalDraftAdapter(
  options: AssistedAdapterOptions,
): PrequalDraftAdapter {
  const { draftId, role, transport } = options;

  // Read fresh every render, consumed at call time. The broker may change the
  // certification choice after the form has mounted, and a closure captured at
  // build time would send whatever was selected first — recording an
  // attestation nobody made, or mailing a copy somebody unchecked.
  const certificationRef = useRef<AssistedCertification>(options.certification);
  certificationRef.current = options.certification;
  const onLoadedRef = useRef(options.onLoaded);
  onLoadedRef.current = options.onLoaded;

  // The concurrency stamp. Held here and nowhere else — the form has no field
  // for it, which is what makes "somebody else wrote to this draft" a real
  // answer instead of a silent overwrite.
  const rowVersionRef = useRef<string | null>(null);

  return useMemo<PrequalDraftAdapter>(() => {
    async function load(): Promise<PrequalDraftLoad | null> {
      // A denial, a purge, or a network failure all land here. There is nothing
      // to rehydrate in any of them, and the surface renders its own error from
      // its own load — this one stays quiet so a transient blip does not wipe
      // answers the broker has already typed.
      try {
        const dto = await transport.load(draftId);
        rowVersionRef.current = dto.rowVersion;
        onLoadedRef.current?.(dto);
        const answers = parseAnswers(dto.answersJson);
        if (answers === null) return null;
        return {
          role: dto.role,
          stepIndex: dto.stepIndex,
          stepCount: dto.stepCount,
          answers,
          identity: {
            firstName: dto.firstName,
            lastName: dto.lastName,
            email: dto.email,
          },
          partnerSlug: dto.partnerSlug ?? null,
        };
      } catch {
        return null;
      }
    }

    async function persistStep(snapshot: PrequalDraftSnapshot): Promise<PrequalDraftWriteResult> {
      // No create branch, ever. An assisted surface is always handed a draft
      // that already exists; the row was minted by the in-office start, which
      // is what makes it INVITED rather than organic and therefore visible on
      // the originating broker's own follow-up list.
      try {
        const persisted = await transport.step(draftId, {
          rowVersion: rowVersionRef.current,
          stepIndex: snapshot.stepIndex,
          answersJson: JSON.stringify(snapshot.answers),
        });
        rowVersionRef.current = persisted.rowVersion;
        return { status: "saved" };
      } catch (error) {
        if (transport.isConflict(error)) {
          return { status: "conflict", message: CONFLICT_MESSAGE };
        }
        return { status: "failed", message: friendly(error) };
      }
    }

    async function finalize(request: PrequalFinalizeRequest): Promise<PrequalFinalizeResult> {
      const answers = request.role === "Seller" ? request.seller : request.buyer;
      const certification = certificationRef.current;

      // Errors are NOT swallowed here. The form's own finalize step renders
      // them, and a validation rejection ten minutes into somebody's client's
      // application is exactly the thing a human needs to read.
      const result = await transport.finalize(draftId, {
        rowVersion: rowVersionRef.current,
        consentAccepted: request.consentAccepted,
        answersJson: JSON.stringify(answers ?? {}),
        phone: request.phone,
        website: request.website,
        completionMode: certification.completionMode,
        suppressClientCopy: certification.suppressClientCopy,
      });

      // The draft is gone — it became the submission in one transaction. Drop
      // the stamp so a stray retry cannot look like a live write.
      rowVersionRef.current = null;
      return result;
    }

    return { mode: "assisted", role, load, persistStep, finalize };
  }, [draftId, role, transport]);
}
